import { datadogRum } from '@datadog/browser-rum';
import { log } from '@/logger/logger.ts';
import { runGuarded } from '@/services/waf/runGuarded';

// Its own logger keeps these events out of the WAF challenge metrics.
// KEEP_IN_SYNC sinatra/generated_monitors/weave_engineering/turnstile.tf: the client blocked-share monitor filters
// on this logger name and on the outcome / unavailableReason values below.
const logger = log.getLogger('SignInTurnstile');

export enum SignInTurnstileTelemetryOutcome {
  ScriptLoaded = 'script-loaded',
  Ready = 'ready',
  Slow = 'slow',
  Unavailable = 'unavailable',
}

export enum SignInTurnstileStage {
  ScriptNotLoaded = 'script-not-loaded',
  WidgetNotRendered = 'widget-not-rendered',
  AwaitingWidgetToken = 'awaiting-widget-token',
}

export enum SignInTurnstileUnavailableReason {
  WidgetUnavailable = 'widget-unavailable',
  WidgetUnsupportedInBrowser = 'widget-unsupported-in-browser',
}

interface SignInTurnstileTelemetryEvent {
  outcome: SignInTurnstileTelemetryOutcome;
  scriptLoadDurationMs?: number;
  /** True when the page already had the Turnstile script, so scriptLoadDurationMs measured no download. */
  wasScriptAlreadyLoaded?: boolean;
  readyDurationMs?: number;
  turnstileStage?: SignInTurnstileStage;
  unavailableReason?: SignInTurnstileUnavailableReason;
  providerErrorCode?: string;
}

const WARN_OUTCOMES = new Set([SignInTurnstileTelemetryOutcome.Slow, SignInTurnstileTelemetryOutcome.Unavailable]);

/** Never throws: telemetry must not break sign-in. */
export function recordSignInTurnstileTelemetry(event: SignInTurnstileTelemetryEvent): void {
  runGuarded(() => {
    logger[WARN_OUTCOMES.has(event.outcome) ? 'warn' : 'info']('Sign-in Turnstile telemetry', {
      ...event,
      pageVisibilityState: document.visibilityState,
    });
  });
  runGuarded(() => {
    if (
      event.outcome !== SignInTurnstileTelemetryOutcome.ScriptLoaded ||
      event.scriptLoadDurationMs === undefined ||
      event.wasScriptAlreadyLoaded === true
    ) {
      return;
    }
    datadogRum.addDurationVital('signin_turnstile_script_load', {
      startTime: Date.now() - event.scriptLoadDurationMs,
      duration: event.scriptLoadDurationMs,
    });
  });
}
